import axios from 'axios'
import { createWithEqualityFn } from 'zustand/traditional'

interface ProductLineStore {
	data: any[]
	selectedWithChildren: any
	selectedWithProperties: any
	options: any[]
	accessories: any[]
	thicknesses: any[]
	outerMaterials: any[]
	innerMaterials: any[]
	printingOptions: any[]
	products: any[]
	loading: boolean
	setSelectedWithChildren: (selected: any) => void
	setSelectedWithProperties: (selected: any) => void
	setLoading: (loading: boolean) => void
	getAll: () => Promise<{ success: boolean; data: any }>
	getOneWithChildren: (id: number) => Promise<{ success: boolean; data: any }>
	getOneWithProperties: (id: number) => Promise<{ success: boolean; data: any }>
	getOptions: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getAccessories: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getThicknesses: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getOuterMaterials: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getInnerMaterials: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getPrintingOptions: (productLineId: number) => Promise<{ success: boolean; data: any }>
	getProducts: (productLineId: number) => Promise<{ success: boolean; data: any }>
	clearState: () => void
}

export const API_SLUG = 'suppliers/product-lines'

export const useProductLineStore = createWithEqualityFn<ProductLineStore>(
	set => ({
		data: [],
		selectedWithChildren: {},
		selectedWithProperties: {},
		options: [],
		accessories: [],
		thicknesses: [],
		outerMaterials: [],
		innerMaterials: [],
		printingOptions: [],
		products: [],
		loading: false,
		setSelectedWithChildren: selected => set({ selectedWithChildren: selected }),
		setSelectedWithProperties: selected => set({ selectedWithProperties: selected }),
		setLoading: (loading: boolean) => set({ loading }),
		getAll: async () => {
			set({ loading: true })
			return axios
				.get(`${API_SLUG}`)
				.then(res => {
					set({ loading: false })
					set({ data: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					set({ loading: false })
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getOneWithChildren: async (id: number) => {
			set({ loading: true })
			return axios
				.get(`${API_SLUG}/${id}/child`)
				.then(res => {
					set({ loading: false })
					set({ selectedWithChildren: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					set({ loading: false })
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getOneWithProperties: async (id: number) => {
			set({ loading: true })
			return axios
				.get(`${API_SLUG}/${id}/properties`)
				.then(res => {
					set({ loading: false })
					set({ selectedWithProperties: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					set({ loading: false })
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getOptions: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/options`)
				.then(res => {
					set({ options: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getAccessories: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/accessories`)
				.then(res => {
					set({ accessories: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getThicknesses: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/thicknesses`)
				.then(res => {
					set({ thicknesses: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getOuterMaterials: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/outer-materials`)
				.then(res => {
					set({ outerMaterials: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getInnerMaterials: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/inner-materials`)
				.then(res => {
					set({ innerMaterials: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getPrintingOptions: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/printing-options`)
				.then(res => {
					set({ printingOptions: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		getProducts: async (productLineId: number) => {
			return axios
				.get(`${API_SLUG}/${productLineId}/products`)
				.then(res => {
					set({ products: res.data })
					return { success: true, data: res.data }
				})
				.catch(err => {
					return { success: false, data: err?.response?.data?.message?.toString() }
				})
		},
		clearState: () =>
			set({
				data: [],
				selectedWithChildren: {},
				selectedWithProperties: {},
				loading: false,
			}),
	}),
	Object.is
)
